import store from '@/store'

/**
 * Holds flags to determine whether a Promise is pending or not.
 *
 * It may handle more than one Promise instance.
 */
export class AsyncState {
  /**
   * If it already handled a Promise.
   */
  didRun = false

  /**
   * Last recorded Error.
   */
  error: Error | null = null

  /**
   * If it is currently handling a Promise.
   */
  isPending = false

  /**
   * Checks if it has an error.
   */
  get hasError () {
    return this.error !== null
  }

  /**
   * Checks if the last handled Promise did resolve.
   */
  get isResolved () {
    return this.didRun && !this.isPending && !this.hasError
  }

  /**
   * Handles a Promise and updates the flags accordingly.
   *
   * It returns the resolved value.
   *
   * @param promise Promise to be resolved.
   */
  async handle<T> (promise: Promise<T>) {
    this.didRun = true

    try {
      this.isPending = true
      const result = await promise
      this.error = null
      return result
    } catch (e) {
      this.error = e
      throw e
    } finally {
      this.isPending = false
    }
  }

  /**
   * Resets the state.
   */
  reset () {
    this.didRun = false
    this.error = null
    this.isPending = false
  }

  /**
   * Triggers the state as if it did run.
   */
  touch () {
    this.didRun = true
  }
}

/**
 * Holds flags to determine a Modal state.
 *
 * It can handle a Promise when the Modal submits something.
 */
export class ModalState extends AsyncState {
  /**
   * If the Modal should be visible.
   */
  private _isActive = false

  /**
   * If the Modal should be visible.
   */
  get isActive () {
    return this._isActive
  }

  set isActive (value) {
    if (value !== this._isActive) {
      store.dispatch('updateModalCount', value)
    }

    this._isActive = value
  }

  async handle<T> (promise: Promise<T>) {
    try {
      const result = await super.handle(promise)
      return result
    } catch (e) {
      this.isActive = true
      throw e
    }
  }

  reset () {
    super.reset()
    this.isActive = false
  }
}
